<script setup lang="ts">
import { formatPrice } from '#shared/utils/currency'

const { transparentHeader } = defineProps<{
  checkout?: Boolean
  transparentHeader?: Boolean
}>()

const cart = useCartStore()

const open = ref(false)

watch(
  () => cart.count,
  (newValue, oldValue) => {
    if (newValue === 0) {
      open.value = false
    }

    if (newValue > oldValue) {
      open.value = true
    }
  },
  { deep: true }
)
</script>

<template>
  <UDrawer
    direction="right"
    v-model:open="open"
    :ui="{ content: 'max-w-[420px]' }"
  >
    <UButton
      color="neutral"
      variant="ghost"
      icon="i-lucide-handbag"
      size="lg"
      :class="['relative px-2', { dark: transparentHeader }]"
    >
      <span
        v-if="cart.count"
        class="absolute top-0.5 right-0.5 min-w-4 h-4 rounded-full flex items-center justify-center text-[10px] shadow-xs"
        :class="[
          { 'bg-stone-50 text-stone-950': transparentHeader },
          { 'bg-stone-950 text-white': !transparentHeader }
        ]"
      >
        {{ cart.count }}
      </span>
    </UButton>

    <template #header>
      <div class="flex justify-between items-center mb-4 md:mt-2.5">
        <h4 class="font-medium text-lg uppercase">Shopping Cart</h4>

        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          icon="i-lucide-x"
          @click="open = false"
        />
      </div>

      <USeparator color="neutral" />
      <p
        class="mt-2 text-xs text-shadow-stone-500 uppercase font-light text-center"
      >
        Free complimentary shipping on all EU orders
      </p>
    </template>

    <template #body>
      <UAlert
        class="mt-4 w-[330px]"
        variant="outline"
        icon="i-heroicons-information-circle"
        v-if="!cart.count"
        title="Your shopping cart is empty"
        description="Looks like you haven’t added anything yet."
      >
      </UAlert>
      <div v-else class="h-full w-full flex flex-col justify-between">
        <div>
          <div
            v-for="item in cart.items"
            class="flex items-center justify-between gap-2 md:gap-3 mt-6"
          >
            <NuxtImg
              :src="item.thumbnail.url"
              alt="alternativeText"
              provider="strapi"
              width="100"
            />

            <div class="w-full">
              <h4 class="font-medium mb-0.5">
                {{ item.product.name }} {{ item.name }}
              </h4>
              <p class="text-sm">
                {{ formatPrice(item.quantity * item.product.price) }}
              </p>
            </div>

            <div class="shrink-1 flex flex-col justify-end items-center gap-1">
              <UFieldGroup size="xs" variant="outline">
                <UButton
                  variant="outline"
                  icon="i-lucide-minus"
                  @click="cart.remove({ ...item })"
                />

                <UInput
                  variant="outline"
                  :value="item.quantity"
                  class="w-7"
                  highlight
                  disabled
                  :ui="{ base: 'text-center' }"
                />

                <UButton
                  variant="outline"
                  icon="i-lucide-plus"
                  @click="cart.add({ ...item })"
                />
              </UFieldGroup>

              <UButton
                color="neutral"
                variant="link"
                size="xs"
                @click="cart.delete(item)"
                class="text-[10px] p-0"
              >
                Remove
              </UButton>
            </div>
          </div>
        </div>

        <footer>
          <USeparator color="neutral" />
          <div
            class="flex justify-between items-center my-4.5 font-medium text-lg"
          >
            SUBTOTAL: <span>{{ formatPrice(cart.total) }}</span>
          </div>

          <UButton block to="/checkout/order-details">View Cart</UButton>
          <p
            class="mt-2 text-[10px] text-shadow-stone-500 uppercase font-light text-center"
          >
            Tax and promo codes are calculated at checkout
          </p>

          <div class="text-center mt-6 md:mb-4">
            <div
              class="flex items-center justify-center gap-2 text-sm text-stone-600 mb-2"
            >
              <UIcon
                name="i-heroicons-shield-check"
                class="size-5 text-green-600"
              />
              <span>
                Secure checkout powered by
                <strong class="text-stone-800">Stripe</strong>
              </span>
            </div>
            <p class="text-xs text-stone-500">
              Your payment information is encrypted and securely processed by
              Stripe. We never store your payment details on our servers.
            </p>
          </div>
        </footer>
      </div>
    </template>
  </UDrawer>
</template>
